"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";

/**
 * Homepage preloader with a real-progress meter.
 *
 *   - The counter ticks up on an exponential curve while the page is
 *     still fetching (approaching ~88% over ~3s) so it always feels
 *     like something is happening.
 *   - Once `window.load` fires (or the hard cap trips), the meter is
 *     pulled smoothly to 100%.
 *   - Only THEN does the "Burak / Ozgur" split reveal + divider +
 *     two-block curtain play — so the hero is fully ready when the
 *     user sees it (no hitch after the name appears).
 *
 * Runs only on `/` and only on the first visit per browser session.
 */
const MIN_MS = 900;
const MAX_MS = 12000;

export default function Preloader() {
  const pathname = usePathname();
  const rootRef = useRef(null);
  const [mounted, setMounted] = useState(true);
  const [progress, setProgress] = useState(0);

  const isHome = pathname === "/";

  useEffect(() => {
    if (!isHome) {
      setMounted(false);
      return;
    }
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("bo:preloader-seen")
    ) {
      setMounted(false);
      return;
    }

    gsap.registerPlugin(CustomEase);
    if (!CustomEase.get("hop")) CustomEase.create("hop", "0.9, 0, 0.1, 1");

    document.body.style.overflow = "hidden";

    const started = Date.now();
    let isLoaded = document.readyState === "complete";
    let outroPlayed = false;
    let rafId = 0;
    let current = 0;

    const onLoad = () => {
      isLoaded = true;
    };
    if (!isLoaded) window.addEventListener("load", onLoad, { once: true });
    const capId = setTimeout(() => {
      isLoaded = true;
    }, MAX_MS);

    const finish = () => {
      document.body.style.overflow = "";
      try {
        sessionStorage.setItem("bo:preloader-seen", "1");
      } catch {}
      // Broadcast that the page is finally interactive. HomeHero (and
      // any other above-the-fold autoplay iframe) listens for this so
      // it can re-attempt autoplay the moment the curtain opens —
      // the attempt that fires on iframe-load while the preloader is
      // still covering everything routinely gets swallowed by iOS
      // Safari's autoplay gate.
      try {
        window.dispatchEvent(new CustomEvent("bo:preloader-done"));
      } catch {}
      // Defer the unmount out of GSAP's onComplete callback so React
      // isn't asked to tear down DOM mid-tween commit.
      queueMicrotask(() => setMounted(false));
    };

    const playOutro = () => {
      try {
        const tl = gsap.timeline({ defaults: { ease: "hop" } });
        tl.to(".bo-pl-count-number", { opacity: 0, duration: 0.35 });
        tl.to(".bo-pl-spinner", { opacity: 0, duration: 0.25 }, "<");
        tl.to(".bo-pl-word h1", { y: "0%", duration: 0.9 }, "-=0.15");
        tl.to(
          ".bo-pl-divider",
          {
            scaleY: "100%",
            duration: 0.9,
            onComplete: () =>
              gsap.to(".bo-pl-divider", {
                opacity: 0,
                duration: 0.3,
                delay: 0.2,
              }),
          },
          "<"
        );
        tl.to("#bo-pl-word-1 h1", { y: "100%", duration: 0.8, delay: 0.25 });
        tl.to("#bo-pl-word-2 h1", { y: "-100%", duration: 0.8 }, "<");
        tl.to(".bo-pl-block", {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
          duration: 0.9,
          stagger: 0.08,
          ease: "hop",
          onComplete: finish,
        });
      } catch (err) {
        // If GSAP or a target selector blows up for any reason, fall
        // back to an immediate unmount instead of stranding the user
        // behind a black curtain.
        // eslint-disable-next-line no-console
        console.warn("[preloader] outro failed, skipping animation", err);
        finish();
      }
    };

    const tick = () => {
      const elapsed = Date.now() - started;
      // While the page is loading, creep upward toward ~88% on an
      // exponential curve so the meter always feels alive.
      const soft = Math.min(88, 88 * (1 - Math.exp(-elapsed / 1400)));
      const target = isLoaded ? 100 : soft;
      // Smooth approach to the target so a sudden "isLoaded" flip
      // doesn't visibly jerk the number.
      current += (target - current) * 0.09;
      setProgress(Math.round(current));

      if (current >= 99.4 && isLoaded && elapsed >= MIN_MS && !outroPlayed) {
        outroPlayed = true;
        setProgress(100);
        playOutro();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(capId);
      window.removeEventListener("load", onLoad);
      document.body.style.overflow = "";
    };
  }, [isHome]);

  if (!mounted || !isHome) return null;

  return (
    <div ref={rootRef} className="bo-pl-root" aria-hidden="true">
      <div className="bo-pl-overlay">
        <div className="bo-pl-block" />
        <div className="bo-pl-block" />
      </div>

      <div className="bo-pl-logo">
        <div className="bo-pl-word" id="bo-pl-word-1">
          <h1>
            <span>Burak</span>
          </h1>
        </div>
        <div className="bo-pl-word" id="bo-pl-word-2">
          <h1>Ozgur</h1>
        </div>
      </div>

      <div className="bo-pl-divider" />

      <div className="bo-pl-spinner-wrap">
        <div className="bo-pl-spinner" />
      </div>

      <div className="bo-pl-counter">
        <h1 className="bo-pl-count-number">
          {String(progress).padStart(2, "0")}
        </h1>
      </div>
    </div>
  );
}
